import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Sparkles, Send } from "lucide-react";

function renderBrief(text) {
  if (!text) return null;
  const sections = ["SITUAZIONE", "PRIORITÀ", "PRIORITA", "RISCHI", "OPPORTUNITÀ", "OPPORTUNITA", "AZIONI CONSIGLIATE", "AZIONI"];
  const lines = text.split("\n");
  const out = [];
  let buf = [];
  let title = null;
  const flush = () => {
    if (title || buf.length) {
      out.push({ title, body: buf.join("\n").trim() });
      buf = []; title = null;
    }
  };
  for (const line of lines) {
    const upper = line.trim().toUpperCase();
    if (sections.includes(upper)) { flush(); title = line.trim(); continue; }
    buf.push(line);
  }
  flush();
  return out.map((s, i) => (
    <div key={i} className="mb-6">
      {s.title && <div className="label-eyebrow mb-2">{s.title}</div>}
      <div className="whitespace-pre-wrap text-[15px] leading-relaxed">{s.body}</div>
    </div>
  ));
}

export default function AIManager() {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [chat, setChat] = useState([]);

  const loadBrief = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/manager-brief");
      setBrief(data);
    } catch (e) {
      toast.error("Errore generazione briefing");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    loadBrief();
    (async () => {
      try {
        const { data } = await api.get("/ai/history");
        setChat(data.slice(0, 20).reverse());
      } catch {}
    })();
  }, []);

  const ask = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    const q = question;
    setQuestion("");
    setAsking(true);
    setChat((c) => [...c, { question: q, answer: null }]);
    try {
      const { data } = await api.post("/ai/ask", { question: q });
      setChat((c) => {
        const copy = [...c];
        copy[copy.length - 1] = { question: q, answer: data.answer };
        return copy;
      });
    } catch (e) {
      toast.error("Errore risposta AI");
      setChat((c) => c.slice(0, -1));
    } finally { setAsking(false); }
  };

  const suggested = [
    "Quanto abbiamo guadagnato questo mese?",
    "Quali sono i prodotti più redditizi?",
    "Cosa devo ordinare?",
    "Come posso aumentare lo scontrino medio?",
  ];

  return (
    <div data-testid="ai-manager-page">
      <PageHeader eyebrow="AI Manager · Oggi" title="Il tuo direttore digitale"
        subtitle="Un riepilogo quotidiano su situazione, priorità, rischi e opportunità, generato dai tuoi dati reali."
        actions={<Button data-testid="refresh-brief" onClick={loadBrief} variant="outline">Rigenera briefing</Button>} />

      <div className="border border-[#E2D9C8] rounded-lg p-8 bg-[#F8F4EB] mb-8" data-testid="ai-brief">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={16} strokeWidth={1.5} />
          <div className="label-eyebrow">Briefing generato · {brief?.date || "…"}</div>
        </div>
        {loading ? <div className="text-neutral-500 text-sm">Generazione in corso…</div> : renderBrief(brief?.brief)}
      </div>

      <h2 className="font-serif text-3xl mb-4">Chiedi qualsiasi cosa</h2>
      <div className="border border-[#E2D9C8] rounded-lg bg-white">
        <div className="p-6 max-h-[500px] overflow-y-auto space-y-4">
          {chat.length === 0 && (
            <div className="text-sm text-neutral-500">Nessuna conversazione ancora. Prova una delle domande suggerite qui sotto.</div>
          )}
          {chat.map((c, i) => (
            <div key={i} className="space-y-2">
              <div className="text-sm"><span className="label-eyebrow mr-2">Tu</span>{c.question}</div>
              <div className="pl-4 border-l-2 border-[#D7C4A3] whitespace-pre-wrap text-[15px] leading-relaxed">
                {c.answer === null ? <span className="text-neutral-500">…</span> : c.answer}
              </div>
            </div>
          ))}
        </div>
        <form onSubmit={ask} className="border-t border-[#E2D9C8] p-4 flex gap-2">
          <Input data-testid="ai-question-input" value={question} onChange={(e) => setQuestion(e.target.value)}
            placeholder="Es. Quali prodotti dovrei promuovere?" disabled={asking} />
          <Button data-testid="ai-ask-btn" type="submit" disabled={asking || !question.trim()}
            className="bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7]">
            <Send size={14} strokeWidth={1.5} />
          </Button>
        </form>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {suggested.map((s) => (
          <button key={s} onClick={() => setQuestion(s)}
            className="text-xs border border-[#E2D9C8] hover:border-neutral-900 rounded-full px-3 py-1 transition-colors">
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
